/**
 * CART DRAWER V2 - SIMPLIFIED UI COMPONENT
 * Single responsibility: Cart drawer UI interactions
 * Target: 150 lines, 12KB
 */

class CartDrawerV2 extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.cartCore = null;
    this.previousFocus = null;
    this.mainContent = document.getElementById('MainContent');
    this.init();
  }

  init() {
    cartV2Log('🚀 Cart Drawer V2 initializing...');
    this.setupEventListeners();
    this.connectToCore();
    cartV2Log('✅ Cart Drawer V2 ready');
  }

  // =============================================================================
  // CORE CONNECTION
  // =============================================================================

  connectToCore() {
    // Wait for cart core to be available
    const checkCore = () => {
      if (window.cartCoreV2) {
        this.cartCore = window.cartCoreV2;
        this.setupCoreListeners();
      } else {
        setTimeout(checkCore, 100);
      }
    };
    checkCore();
  }

  setupCoreListeners() {
    // Listen for drawer state changes
    this.cartCore.on('cart:drawer-opened', () => {
      this.open();
    });

    this.cartCore.on('cart:drawer-closed', () => {
      this.close();
    });

    // Listen for cart updates to refresh content
    this.cartCore.on('cart:state-changed', (state) => {
      cartV2Log('🔄 Cart state changed, updating drawer content:', state);
      this.updateContent(state);
      // When network is idle, ensure placeholders are cleared
      if (!state?.isLoading) {
        this.clearPlaceholders();
      }
    });
    
    // No separate render on item-added; rely on unified cart:state-changed to avoid double re-render
    this.cartCore.on('cart:item-added', (data = {}) => {
      cartV2Log('🛒 Item added to cart:', data);
    });

    // Error path
    this.cartCore.on('cart:error', () => {
      this.clearPlaceholders();
    });

    // Immediately sync current state in case we missed prior events
    try {
      const current = this.cartCore.getState ? this.cartCore.getState() : null;
      if (current) {
        this.updateContent(current);
        // If core already marked drawer open before we connected, open now
        if (current.isOpen && !this.isOpen) {
          this.open();
        }
      }
    } catch (_) {}
  }

  // =============================================================================
  // DRAWER CONTROL
  // =============================================================================

  open() {
    if (window.RuemoodShopping?.enabled) { this.isOpen = true; window.RuemoodShopping.openCart(); return; }
    if (this.isOpen) return;
    
    cartV2Log('🚪 Opening cart drawer...');
    this.isOpen = true;
    
    // Add CSS classes for animation
    this.classList.add('animate', 'active');
    // Unified scroll lock: toggle on both html and body
    document.body.classList.add('scroll-locked');
    document.documentElement.classList.add('scroll-locked');
    
    // Accessibility: mark dialog visible and background inert
    const dialog = this.querySelector('.drawer__inner');
    if (dialog) {
      dialog.setAttribute('aria-hidden', 'false');
    }
    if (this.mainContent) {
      this.mainContent.setAttribute('inert', '');
      this.mainContent.setAttribute('aria-hidden', 'true');
    }
    
    // Debug: Check if classes were added
    cartV2Log('🎨 Drawer classes after opening:', Array.from(this.classList));
    cartV2Log('🎨 Scroll locked (html/body):', document.documentElement.classList.contains('scroll-locked'), document.body.classList.contains('scroll-locked'));
    
    // Debug: Check drawer position and visibility (only when debug is on)
    if (window && window.debugCart) {
      const rect = this.getBoundingClientRect();
      cartV2Log('🎨 Drawer position:', {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left,
        right: rect.right,
        visible: rect.width > 0 && rect.height > 0
      });
    }
    
    // Debug removed - drawer is working!
    
    // Focus management (defer trap to next frame for smoother open)
    this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    requestAnimationFrame(() => this.trapFocus());
    
    // Ensure content reflects latest state when opening
    try {
      if (this.cartCore && typeof this.cartCore.getState === 'function') {
        this.updateContent(this.cartCore.getState());
      }
    } catch (_) {}
    
    // Emit event for tracking
    this.dispatchEvent(new CustomEvent('cart-drawer:opened'));
  }

  close() {
    if (window.RuemoodShopping?.enabled) { this.isOpen = false; window.RuemoodShopping.closeCart(); return; }
    if (!this.isOpen) return;
    
    cartV2Log('🚪 Closing cart drawer...');
    this.isOpen = false;
    
    // Remove CSS classes
    this.classList.remove('active');
    document.body.classList.remove('scroll-locked');
    document.documentElement.classList.remove('scroll-locked');
    
    // Accessibility: hide dialog and release background
    const dialog = this.querySelector('.drawer__inner');
    if (dialog) {
      dialog.setAttribute('aria-hidden', 'true');
    }
    if (this.mainContent) {
      this.mainContent.removeAttribute('inert');
      this.mainContent.removeAttribute('aria-hidden');
    }
    
    // Release focus
    this.releaseFocus();
    // Restore focus to previous trigger
    if (this.previousFocus && document.contains(this.previousFocus)) {
      try { this.previousFocus.focus(); } catch (e) {}
    }
    
    // Clean up animation class after transition
    setTimeout(() => {
      this.classList.remove('animate');
    }, 300);
    
    // Emit event for tracking
    this.dispatchEvent(new CustomEvent('cart-drawer:closed'));
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  // =============================================================================
  // CONTENT MANAGEMENT
  // =============================================================================

  updateContent(cartState) {
    if (window.RuemoodShopping?.enabled) { window.RuemoodShopping.cartStateChanged(cartState); return; }
    cartV2Log('🎨 Updating drawer content with state:', cartState);
    
    const itemsContainer = this.querySelector('.cart-drawer__items');
    const emptyState = this.querySelector('.cart-drawer__empty');
    const footer = this.querySelector('.cart-drawer__footer');

    if (!cartState || cartState.itemCount === 0) {
      // Show empty state
      cartV2Log('📭 Showing empty cart state');
      // Clear items container to avoid stale content while relying on CSS visibility
      if (itemsContainer) itemsContainer.innerHTML = '';
      this.classList.add('is-empty');
    } else {
      // Show cart items
      cartV2Log('🛒 Showing cart items:', cartState.items.length);
      this.classList.remove('is-empty');
      
      this.renderItems(cartState.items);
      this.updateTotals(cartState);
    }
  }

  renderItems(items) {
    const container = this.querySelector('.cart-drawer__items');
    if (!container) {
      cartV2Log('⚠️ Cart items container not found');
      return;
    }

    if (!items || items.length === 0) {
      container.innerHTML = '<p class="cart-empty-message">Your cart is empty</p>';
      return;
    }

    // Enhanced item rendering with better error handling
    const containerScrollTop = container.scrollTop || 0;
    const markup = items.map(item => {
      const imageUrl = item.image || item.featured_image?.url || '';
      let title = item.title || item.product_title || 'Unknown Product';
      
      // Remove ALL size/variant suffixes from title to prevent duplication
      // Match patterns like "- Large", "- XL", "- 4XL / Black", etc.
      title = title.replace(/\s*-\s*([^-]+\s*\/\s*)?[^-]*$/i, '').trim();
      // Also remove any trailing size patterns
      title = title.replace(/\s+(XS|S|M|L|XL|XXL|XXXL|2XL|3XL|4XL|5XL|6XL|Small|Medium|Large|Extra Large)$/i, '');
      
      const variantTitle = item.variant_title || '';
      const price = item.price || item.final_price || 0;
      const linePrice = (item.final_line_price || (price * (item.quantity || 1)));
      
      // Prefer pending debounced quantity if present to avoid UI bouncing
      let quantity = item.quantity || 1;
      try {
        if (this.cartCore && this.cartCore.pendingQty && (this.cartCore.pendingQty.has(item.key) || this.cartCore.pendingQty.has(item.id))) {
          const pending = this.cartCore.pendingQty.get(item.key) ?? this.cartCore.pendingQty.get(item.id);
          if (typeof pending === 'number') quantity = pending;
        }
      } catch (_) {}
      const key = item.key || item.id;
      const optimistic = !!item.__optimistic;
      const identity = item.__identity || '';
      const itemClasses = ['cart-item'];
      if (optimistic) itemClasses.push('cart-item--optimistic');

      return `
        <div class="${itemClasses.join(' ')}" data-key="${key}"${identity ? ` data-identity="${identity}"` : ''}${optimistic ? ' aria-busy="true"' : ''}>
          <div class="cart-item__image" aria-hidden="${imageUrl ? 'false' : 'true'}" style="width:70px;height:70px;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:#f3f4f6;border-radius:6px;overflow:hidden;">
            ${imageUrl ? `<img src="${imageUrl}" alt="${title}" width="70" height="70" style="width:100%;height:100%;object-fit:cover;" decoding="async">` : ''}
          </div>
          <div class="cart-item__details">
            <div class="cart-item__header">
              <h3 class="cart-item__title">${title}</h3>
              <div class="cart-item__price">${this.formatPrice(linePrice)}</div>
            </div>
            ${variantTitle ? `<p class="cart-item__variant">${variantTitle}</p>` : ''}
            <div class="cart-item__controls">
              <div class="cart-item__quantity">
                <button class="quantity-btn" data-action="decrease" data-key="${key}" ${quantity <= 1 ? 'disabled' : ''}>-</button>
                <span class="quantity-value">${quantity}</span>
                <button class="quantity-btn" data-action="increase" data-key="${key}">+</button>
              </div>
              <button class="cart-item__remove" data-key="${key}" title="Remove item" aria-label="Remove item">
                <span class="svg-wrapper" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-remove" viewBox="0 0 16 16" focusable="false">
                    <path fill="currentColor" d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1M6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02m4.84 11.52h-7.5V4h7.5z"></path>
                    <path fill="currentColor" d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5m2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5"></path>
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    if (container.dataset.currentMarkup === markup) {
      cartV2Log('🎨 Drawer content unchanged, skipping re-render');
      return;
    }

    container.dataset.currentMarkup = markup;
    container.innerHTML = markup;
    // Restore scroll to preserve position and avoid jumpiness
    try { container.scrollTop = containerScrollTop; } catch (_) {}
    
    cartV2Log('🎨 Rendered', items.length, 'cart items');
  }

  updateTotals(cartState) {
    const totalElement = this.querySelector('.cart-drawer__total-price');
    if (totalElement && cartState.totalPrice !== undefined) {
      totalElement.textContent = this.formatPrice(cartState.totalPrice);
      cartV2Log('💰 Updated cart total to:', this.formatPrice(cartState.totalPrice));
    }
  }

  formatPrice(cents) {
    // Get currency from Shopify's cart or theme settings
    const currency = window.Shopify?.currency?.active || 
                     document.documentElement.getAttribute('data-currency') ||
                     'USD';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'code'  // Shows 'SGD', 'TWD', etc. instead of symbols
    }).format(cents / 100);
  }

  // =============================================================================
  // EVENT HANDLING
  // =============================================================================

  setupEventListeners() {
    // Close button and click outside to close
    this.addEventListener('click', (e) => {
      // If clicking the close button or any element within it, close the drawer
      if (e.target.closest('.drawer__close')) {
        this.cartCore?.closeDrawer();
        return;
      }

      // Empty state continue shopping button should simply close the drawer
      if (e.target.closest('.cart-drawer__continue-shopping')) {
        e.preventDefault();
        this.cartCore?.closeDrawer();
        return;
      }

      // Click outside drawer content to close (click on overlay)
      if (e.target === this && !e.target.closest('.drawer__inner')) {
        this.cartCore?.closeDrawer();
        return;
      }
    });

    // Quantity buttons (debounced optimistic scheduling)
    this.addEventListener('click', (e) => {
      if (e.target.matches('.quantity-btn')) {
        const key = e.target.dataset.key;
        const action = e.target.dataset.action;
        const currentQty = parseInt(e.target.parentElement.querySelector('.quantity-value').textContent);
        const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);

        // Instant UI feedback: update the badge immediately
        const qtyEl = e.target.parentElement.querySelector('.quantity-value');
        if (qtyEl) qtyEl.textContent = newQty;

        // If reaching 0, remove the row instantly for a jiffy feel
        if (newQty === 0) {
          const row = e.target.closest('.cart-item');
          if (row) row.remove();
        }

        if (this.cartCore && typeof this.cartCore.scheduleQuantity === 'function') {
          this.cartCore.scheduleQuantity(key, newQty);
        } else {
          this.cartCore?.updateQuantity(key, newQty);
        }
      }
    });

    // Remove buttons (optimistic scheduling -> qty 0)
    this.addEventListener('click', (e) => {
      const removeButton = e.target.closest('.cart-item__remove');
      if (removeButton) {
        const key = removeButton.dataset.key;
        // Let core state drive rendering; avoid manual DOM removals that cause flicker
        if (this.cartCore && typeof this.cartCore.scheduleQuantity === 'function') {
          this.cartCore.scheduleQuantity(key, 0);
        } else {
          this.cartCore?.removeItem(key);
        }
      }
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.cartCore?.closeDrawer();
      }
    });

  }

  // =============================================================================
  // PLACEHOLDER MANAGEMENT (OPTIMISTIC UI)
  // =============================================================================

  clearPlaceholders() {
    // No-op retained for backward compatibility with core events
  }

  // =============================================================================
  // ACCESSIBILITY
  // =============================================================================

  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    // Focus first element
    firstElement.focus();
    
    // Trap focus within drawer
    this.focusTrapHandler = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };
    
    document.addEventListener('keydown', this.focusTrapHandler);
  }

  releaseFocus() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }
}

// =============================================================================
// INITIALIZATION
// =============================================================================

// Debug logger (gated by window.debugCart)
function cartV2Log(...args) {
  try {
    if (window && window.debugCart) console.log(...args);
  } catch (e) {}
}

// Always define the custom element (V2 is default now)
cartV2Log('🚀 Defining Cart Drawer V2 custom element...');

if (!customElements.get('cart-drawer-v2')) {
  customElements.define('cart-drawer-v2', CartDrawerV2);
  cartV2Log('✅ Cart Drawer V2 custom element defined');
} else {
  cartV2Log('ℹ️ Cart Drawer V2 custom element already defined');
}
  
// =============================================================================
// CART BUTTON SETUP (Always runs since V2 is default)
// =============================================================================

// Setup cart button click handler with retry logic
const setupCartButton = () => {
  const cartButton = document.querySelector('[data-cart-button]');
  if (cartButton && window.cartCoreV2) {
    cartV2Log('🔗 Connecting cart button to V2 drawer...');
    
    // Remove any existing listeners
    if (cartButton._v2ClickHandler) {
      cartButton.removeEventListener('click', cartButton._v2ClickHandler);
    }
    
    // Add new V2 click handler
    cartButton._v2ClickHandler = (e) => {
      e.preventDefault();
      cartV2Log('🖱️ Cart button clicked - opening V2 drawer');
      window.cartCoreV2.openDrawer();
    };
    
    cartButton.addEventListener('click', cartButton._v2ClickHandler);
    cartV2Log('✅ Cart button connected to V2 system');
    
    // Update debug info
    cartV2Log('🔄 Cart button handler attached - debug info updated');
  } else {
    cartV2Log('⏳ Cart button or core not ready, retrying...', {
      cartButton: !!cartButton,
      cartCoreV2: !!window.cartCoreV2
    });
    setTimeout(setupCartButton, 500);
  }
};

// Try to setup immediately and on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupCartButton);
} else {
  setupCartButton();
}

// Also try when cart system is ready
document.addEventListener('cart-system-v2:ready', setupCartButton);
document.addEventListener('cart-core-v2:ready', setupCartButton);

// Aggressive retry - try every 2 seconds for 10 seconds
let retryCount = 0;
const aggressiveSetup = () => {
  const cartButton = document.querySelector('[data-cart-button]');
  if (cartButton && !cartButton._v2ClickHandler && window.cartCoreV2 && retryCount < 5) {
    cartV2Log(`🔄 Aggressive cart button setup attempt ${retryCount + 1}`);
    setupCartButton();
  }
  retryCount++;
  if (retryCount < 5) {
    setTimeout(aggressiveSetup, 2000);
  }
};

// Start aggressive setup after 1 second
setTimeout(aggressiveSetup, 1000);

cartV2Log('🎨 Cart Drawer V2 module loaded');